// Modules
import React, { useState, useEffect, useContext } from "react";
import Avatar from "@material-ui/core/Avatar";
import Skeleton from "@material-ui/lab/Skeleton";
import { useLocation } from "react-router-dom";
import { ElfsightWidget } from "react-elfsight-widget";

// Hooks
import useInterval from "../hooks/useInterval";
import PropTypes from "prop-types";

// Context
import ApiContext from "../../contexts/ApiContext";

// Local
import LoginHome from "../10 - Login";
import Tooltip from "../utils/Tooltip";

// Assets
import "./modal-action.scss";
import CloseIcon from "../../assets/img/svg/Close_icon.jsx";

const ModalAction = (props) => {
  const {
    idFiche,
    data,
    loader,
    showCTA,
    setShowCTA,
    proposeur,
  } = props;
  const {
    num_fiche,
    prenom_match_maker,
    photo_match_maker,
    photo_charge_projet,
    nom_prenom_cdp,
    propositions,
  } = data;

  const { isMobile, isTablet } = useContext(ApiContext);
  const location = useLocation();
  const [createdAt, setCreatedAt] = useState(new Date().getTime());
  const [relativeDate, setRelativeDate] = useState("à l'instant");
  const [timer, setTimer] = useState(0);
  const [showLogin, setShowLogin] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [idxPropo, setIdxPropo] = useState(0);

  const openClose = () => setShowCTA(!showCTA);
  const handleShowLogin = () => setShowLogin(!showLogin);

  /**
   * Sets the correct timing counter in the modal.
   * Triggered at reception of the URL parameter "etad"
   */
  useEffect(() => {
    const params = location.search;
    let hashedTimestamp = params.split("&etad=")[1];

    if (hashedTimestamp === undefined)
      return;
    if (hashedTimestamp.includes("/") === true)
      hashedTimestamp = hashedTimestamp.replace("/", "");

    if (hashedTimestamp !== undefined && hashedTimestamp !== "") {
      const decodedTimestamp = parseInt(atob(hashedTimestamp));

      if (Number.isNaN(decodedTimestamp) === false)
        setCreatedAt(decodedTimestamp * 1000);
    }
  }, [location]);

  /**
   * Chooses the correct photo and name to display if the match-maker properties are arrays,
   * based on the id of the proposal which is defined in the URL.
   * Triggered on reception of the URL param + the properties sent by the API.
   */
  useEffect(() => {
    if (propositions === null && prenom_match_maker && prenom_match_maker.length > 0)
      setIdxPropo(prenom_match_maker.length - 1);
    if (propositions === undefined || propositions === null)
      return;

    const params = location.search;
    let propositionID = params.split("?proposition=")[1];

    if (propositionID === undefined) {
      if (typeof (prenom_match_maker) !== "string")
        setIdxPropo(prenom_match_maker.length - 1);
      return;
    }

    // If we have the two regular parameters "proposition=" and "etad="
    if (propositionID.includes("&etad="))
      propositionID = propositionID.split("&etad=")[0];
    // If we only have the "proposition=" parameter
    else if (propositionID.includes("/"))
      propositionID = propositionID.split("/")[0];

    const indexProposition = propositions?.findIndex((elem) => elem === propositionID);

    if (indexProposition)
      setIdxPropo(indexProposition);
  }, [location, propositions, prenom_match_maker]);

  /**
   * componentDidMount() that listens to scrolling events and helps
   */
  useEffect(() => {
    const onScrollStartHandler = () => {
      setIsScrolling(true);
    };

    const onScrollEndHandler = () => {
      setIsScrolling(false);
    };

    if (isMobile === true || isTablet === true) {
      document.addEventListener("touchstart", onScrollStartHandler);
      document.addEventListener("touchend", onScrollEndHandler);
    }

    return () => {
      if (isMobile === false && isTablet === false)
        return;
      document.removeEventListener("touchstart", onScrollStartHandler);
      document.removeEventListener("touchend", onScrollEndHandler);
    }
  }, []); // eslint-disable-line

  /**
   * Computes relative time from the moment the property
   * was created in our records to now.
   * @param {Number} timestamp 
   * @returns {String} : relative time from now
   */
  const getHumanTime = (timestamp, timer) => {
    // Convert to a positive integer
    let time = Math.abs(timestamp);
    // Define humanTime and units
    let humanTime, units;

    // If there are years
    if (time > (1000 * 60 * 60 * 24 * 365)) {
      humanTime = parseInt(time / (1000 * 60 * 60 * 24 * 365), 10);
      units = humanTime > 1 ? "ans" : "an";
      setTimer(null);
    }
    // If there are months
    else if (time > (1000 * 60 * 60 * 24 * 30)) {
      humanTime = parseInt(time / (1000 * 60 * 60 * 24 * 30), 10);
      units = "mois";
      setTimer(null);
    }
    // If there are weeks
    else if (time > (1000 * 60 * 60 * 24 * 7)) {
      humanTime = parseInt(time / (1000 * 60 * 60 * 24 * 7), 10);
      units = humanTime > 1 ? "semaines" : "semaine";
      setTimer(null);
    }
    // If there are days
    else if (time > (1000 * 60 * 60 * 24)) {
      humanTime = parseInt(time / (1000 * 60 * 60 * 24), 10);
      units = humanTime > 1 ? "jours" : "jour";
      setTimer(null);
    }
    // If there are hours
    else if (time > (1000 * 60 * 60)) {
      humanTime = parseInt(time / (1000 * 60 * 60), 10);
      units = humanTime > 1 ? "heures" : "heure";
      setTimer(60000);
    }
    // If there are minutes
    else if (time > (1000 * 60)) {
      humanTime = parseInt(time / (1000 * 60), 10);
      units = humanTime > 1 ? "minutes" : "minute";
      setTimer(60000);
    }
    // Otherwise, use seconds
    else {
      humanTime = parseInt(time / (1000), 10);
      units = humanTime > 1 ? "secondes" : "seconde";
      setTimer(1000);
    }
    if (humanTime === 0)
      return "à l'instant";
    else
      return `il y a ${humanTime} ${units}`;
  };

  useInterval(() => {
    if (timer !== null && createdAt !== null) {
      const now = new Date().getTime();
      const howLongAgo = createdAt - now;

      setRelativeDate(getHumanTime(howLongAgo, timer));
    }
  }, timer);

  return (
    <React.Fragment>
      <div className={`modal-action-container ${showCTA === true ? "" : "reduced"} ${isMobile === true || isTablet === true ? "mobile" : ""}`}>
        {/* Full card informations */}
        {showCTA === true
          ? (
            <div className="full-card-container">
              <div className="first-row">
                <CloseIcon
                  clssName="close-icon"
                  onClick={openClose}
                />
                {loader
                  ? (
                    <Skeleton
                      variant="circle"
                      width="9.5vh"
                      height="9.5vh"
                      style={{ marginTop: "10px" }}
                    />
                  ) : (
                    <Avatar
                      alt="large-avatar"
                      src={proposeur === "Chargé de projet"
                        ? photo_charge_projet
                        : proposeur !== "Chargé de projet" && photo_match_maker && typeof (photo_match_maker) === "string"
                          ? photo_match_maker
                          : proposeur !== "Chargé de projet" && photo_match_maker && photo_match_maker.length > 0
                            ? photo_match_maker[idxPropo]
                            : photo_charge_projet}
                      width="9.5vh"
                      height="9.5vh"
                      className="custom-avatar"
                    />
                  )}
              </div>

              <div className="second-row">
                {loader
                  ? (
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width="14vh"
                      style={{ margin: "2vh auto" }}
                      className="suggested-by"
                    />
                  ) : (
                    <React.Fragment>
                      <h6 className="suggested-by">
                        {`Proposé par 
                          ${proposeur === "Chargé de projet"
                            ? nom_prenom_cdp
                            : proposeur !== "Chargé de projet" && prenom_match_maker && typeof (prenom_match_maker) === "string"
                              ? prenom_match_maker
                              : proposeur !== "Chargé de projet" && prenom_match_maker && prenom_match_maker.length > 0
                                ? prenom_match_maker[idxPropo]
                                : nom_prenom_cdp
                          } ${relativeDate}`}
                      </h6>
                      <Tooltip
                        tooltipText="Les opportunités correspondant à cette stratégie disparaissent du marché en moyenne en 43h"
                        loader={loader}
                        className="modal-action-tooltip"
                        isScrolling={isScrolling}
                        keyProp="opportunity"
                      />
                    </React.Fragment>
                  )}
              </div>

              <div className="third-row">
                <button className="custom-button" onClick={handleShowLogin}>
                  Faire une offre
                </button>
              </div>

              <div className="fourth-row">
                <button className="custom-button" onClick={handleShowLogin}>Poser une question sur le bien</button>
              </div>

              <div className="fifth-row">
                <ElfsightWidget widgetID="657c87bc-1023-451a-bfc9-24f307e6057c" />
              </div>
            </div>
          ) : (
            /* Reduced display with only avatar */
            <div className="modal-reduced">
              {loader === true
                ? (
                  <Skeleton
                    variant="circle"
                    width="9.5vh"
                    height="9.5vh"
                  />
                ) : (
                  <Avatar
                    alt="large-avatar"
                    onClick={openClose}
                    src={proposeur === "Chargé de projet"
                      ? photo_charge_projet
                      : proposeur !== "Chargé de projet" && photo_match_maker && typeof (photo_match_maker) === "string"
                        ? photo_match_maker
                        : proposeur !== "Chargé de projet" && photo_match_maker && photo_match_maker.length > 0
                          ? photo_match_maker[idxPropo]
                          : photo_charge_projet}
                    width="9.5vh"
                    height="9.5vh"
                    className="large-avatar"
                  />
                )
              }
            </div>
          )}
      </div>

      {/* onClick of "Faire une offre", opens a modal */}
      {showLogin
        ? (
          <LoginHome
            id_fiche={idFiche}
            fiche={num_fiche}
            showLogin={showLogin}
            handleShowLogin={handleShowLogin}
          />
        ) : null}
    </React.Fragment>
  );
};

ModalAction.propTypes = {
  loader: PropTypes.bool.isRequired,
  idFiche: PropTypes.string,
  data: PropTypes.object.isRequired,
  showCTA: PropTypes.bool.isRequired,
  setShowCTA: PropTypes.func.isRequired,
  proposeur: PropTypes.string.isRequired,
};

ModalAction.defaultProps = {
  idFiche: null,
};

export default ModalAction;
